import * as React from 'react';

import { BlitzWelcome } from '@entities/welcome/components/blitz-welcome';
import Com2Welcome from '@entities/welcome/components/com2-welcome/com2-welcome';
import { Intro } from '@entities/welcome/components/intro';
import Ngu3Welcome from '@entities/welcome/components/ngu3-welcome/ngu3-welcome';
import { QuestGuide } from '@entities/welcome/components/quest-guide';
import { ShopQuests } from '@entities/welcome/components/shop-quests';
import { Sk2Welcome } from '@entities/welcome/components/sk2-welcome';
import { TradeLink } from '@entities/welcome/components/trade-link';

import { config } from '@/config.ts';

export type WelcomeStep = {
	component: React.ComponentType<any>;
	props?: Record<string, any>;
};

export type WelcomeFlow = {
	steps: ((handlers: WelcomeHandlers) => WelcomeStep)[];
	totalPages: number;
};

export type WelcomeHandlers = {
	onClickNext: () => void;
	onClickPrevious: () => void;
};

type BrandWelcomeConfig = Record<string, WelcomeFlow>;

/**
 * Map of welcome flows for each brand
 * Each brand can have its own sequence of welcome screens
 */
export const brandWelcomeMap: BrandWelcomeConfig = {
	default: {
		steps: [
			(handlers) => ({ component: Intro, props: { onClickNext: handlers.onClickNext } }),
			(handlers) => ({ component: QuestGuide, props: handlers }),
			(handlers) => ({ component: ShopQuests, props: handlers }),
			(handlers) => ({ component: TradeLink, props: { onClickPrevious: handlers.onClickPrevious } })
		],
		totalPages: 4
	},
	blitz: {
		steps: [
			(handlers) => ({ component: BlitzWelcome, props: { slide: 1, ...handlers } }),
			(handlers) => ({ component: BlitzWelcome, props: { slide: 2, ...handlers } }),
			(handlers) => ({ component: BlitzWelcome, props: { slide: 3, ...handlers } })
		],
		totalPages: 3
	},
	sk2: {
		steps: [
			(handlers) => ({ component: Sk2Welcome, props: { slide: 1, ...handlers } }),
			(handlers) => ({ component: Sk2Welcome, props: { slide: 2, ...handlers } })
		],
		totalPages: 2
	},
	rockets: {
		steps: [
			(handlers) => ({ component: Intro, props: { onClickNext: handlers.onClickNext } }),
			(handlers) => ({ component: QuestGuide, props: handlers }),
			(handlers) => ({ component: TradeLink, props: { onClickPrevious: handlers.onClickPrevious } })
		],
		totalPages: 3
	},
	rockets_vip: {
		steps: [
			(handlers) => ({ component: Intro, props: { onClickNext: handlers.onClickNext } }),
			(handlers) => ({ component: QuestGuide, props: handlers }),
			(handlers) => ({ component: TradeLink, props: { onClickPrevious: handlers.onClickPrevious } })
		],
		totalPages: 3
	},
	ngu3: {
		steps: [
			(handlers) => ({ component: Ngu3Welcome, props: { slide: 1, ...handlers } }),
			(handlers) => ({ component: Ngu3Welcome, props: { slide: 2, ...handlers } }),
			(handlers) => ({ component: Ngu3Welcome, props: { slide: 3, ...handlers } }),
			(handlers) => ({ component: TradeLink, props: { onClickPrevious: handlers.onClickPrevious } })
		],
		totalPages: 4
	},
	com2: {
		steps: [
			(handlers) => ({ component: Com2Welcome, props: { slide: 1, ...handlers } }),
			(handlers) => ({ component: Com2Welcome, props: { slide: 2, ...handlers } }),
			(handlers) => ({ component: Com2Welcome, props: { slide: 3, ...handlers } }),
			(handlers) => ({ component: TradeLink, props: { onClickPrevious: handlers.onClickPrevious } })
		],
		totalPages: 4
	},
	nomad: {
		steps: [
			(handlers) => ({ component: Intro, props: { onClickNext: handlers.onClickNext } }),
			(handlers) => ({ component: QuestGuide, props: handlers }),
			(handlers) => ({ component: ShopQuests, props: handlers }),
			(handlers) => ({ component: TradeLink, props: { onClickPrevious: handlers.onClickPrevious } })
		],
		totalPages: 4
	}
};

/**
 * Get welcome flow configuration for the current brand
 * @returns WelcomeFlow - Welcome flow configuration for current brand
 */
export const getBrandWelcome = (): WelcomeFlow => {
	const brand = config.BRAND || 'default';
	return brandWelcomeMap[brand] || brandWelcomeMap.default;
};

